import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {ActionIcon, SimpleGrid, Tooltip} from "@mantine/core";
import {modals} from "@mantine/modals";
import {notifications} from "@mantine/notifications";
import {Text} from "@mantine/core";
import slugify from "@sindresorhus/slugify";
import {dibAppApi} from "../../dibAppApi/dibAppApi";
import type {TopicItemDto, UserCollectionItem} from "../../dibAppApi/dibAppApi";
import {chatApi} from "../chat/chatApi";
import {TOPIC_TYPE_PATHS} from "../../paths";
import {USER_THREADS_PAGE_SIZE} from "../../types";
import {getUrlForItem} from "../../utils/dibUrls";
import {getDibBaseUrl} from "../../utils/dibUrls";
import {DashboardLinkList} from "../../components/DashboardLinkList/DashboardLinkList";
import type {LinkListItem} from "../../components/DashboardLinkList/DashboardLinkList";
import IconPin from "../../assets/pin.svg?react";
import IconPinFilled from "../../assets/pin-filled.svg?react";

const CHECKLIST_PATH = TOPIC_TYPE_PATHS[47];

function getCollectionUrl(item: UserCollectionItem) {
    const topicPart = `/${CHECKLIST_PATH}/${slugify(item.name)}/${encodeURIComponent(item.topic_id.toLowerCase())}`;
    const collectionPart = `/utfylling/${slugify(item.title)}/${encodeURIComponent(item.collection_id.toLowerCase())}`;
    return `${getDibBaseUrl()}${topicPart}${collectionPart}`;
}

function PinButton({item, isPinned, onPin, onUnpin}: {
    item: TopicItemDto;
    isPinned: boolean;
    onPin: () => void;
    onUnpin: () => void;
}) {
    const {t} = useTranslation();
    const label = isPinned
        ? t("Dashboard.removeFromTop", {defaultValue: "Remove from top"})
        : t("Dashboard.pinToTop", {defaultValue: "Pin to top"});
    return (
        <Tooltip label={label}>
            <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label={label}
                onClick={isPinned ? onUnpin : onPin}
            >
                {isPinned
                    ? <IconPinFilled color="var(--dib-brand-base)" width={16} height={16} />
                    : <IconPin width={16} height={16} />
                }
            </ActionIcon>
        </Tooltip>
    );
}

export function MyWorkConsolidatedPage() {
    const {t} = useTranslation();
    const {data: collectionsData, isLoading: collectionsLoading} =
        dibAppApi.endpoints.getUserCollections.useQuery();
    const {data: workItems, isLoading: workItemsLoading} =
        dibAppApi.endpoints.getWorkItems.useQuery();
    const historyData = workItems?.history;
    const favoritesData = workItems?.favorites;
    const {data: threadsData, isLoading: threadsLoading} =
        chatApi.endpoints.getUserThreads.useQuery({page: 0, pageSize: USER_THREADS_PAGE_SIZE});

    const [patchFollow] = dibAppApi.endpoints.patchDocumentFollow.useMutation();
    const [deleteFollow] = dibAppApi.endpoints.deleteDocumentFollow.useMutation();

    const handlePin = (item: TopicItemDto) => {
        if (!item.id) return;
        patchFollow({topicId: item.id}).unwrap().catch(() => {
            notifications.show({
                message: t("Error.SomethingWentWrong"),
                color: "red",
            });
        });
    };

    const handleUnpin = (item: TopicItemDto) => {
        if (!item.id) return;
        const topicId = item.id;
        modals.openConfirmModal({
            title: t("Dashboard.removeFavorite.title", {
                defaultValue: "Remove from favorites",
            }),
            children: <Text size="lg">{item.name}</Text>,
            labels: {
                confirm: t("Word.Remove", {defaultValue: "Remove"}),
                cancel: t("Word.Abort"),
            },
            confirmProps: {color: "var(--dib-brand-base)"},
            onConfirm: () => {
                deleteFollow({topicId}).unwrap().catch(() => {
                    notifications.show({
                message: t("Error.SomethingWentWrong"),
                color: "red",
            });
                });
            },
        });
    };

    const mittArbeidItems = useMemo<LinkListItem[]>(() => {
        return (collectionsData?.items ?? []).map((item) => ({
            href: getCollectionUrl(item),
            name: item.title,
            description: item.name,
            date: item.lastuse,
        }));
    }, [collectionsData]);

    const innholdItems = useMemo<LinkListItem[]>(() => {
        const pinnedIds = new Set((favoritesData?.items ?? []).map((i) => i.id));

        const pinned: LinkListItem[] = (favoritesData?.items ?? []).map((item) => ({
            ...getUrlForItem(item),
            name: item.name,
            sectionLabel: t("Widget.internal.portal.pinned"),
            rightSection: (
                <PinButton
                    item={item}
                    isPinned={true}
                    onPin={() => handlePin(item)}
                    onUnpin={() => handleUnpin(item)}
                />
            ),
        }));

        const history: LinkListItem[] = (historyData?.items ?? [])
            .filter((item) => !pinnedIds.has(item.id))
            .sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""))
            .map((item) => ({
                ...getUrlForItem(item),
                name: item.name,
                date: item.date,
                rightSection: (
                    <PinButton
                        item={item}
                        isPinned={false}
                        onPin={() => handlePin(item)}
                        onUnpin={() => handleUnpin(item)}
                    />
                ),
            }));

        return [...pinned, ...history];
    }, [historyData, favoritesData, t]);

    const sisteTråderItems = useMemo<LinkListItem[]>(() => {
        return (threadsData?.items ?? []).map((thread) => ({
            to: `/chat/${thread.threadId}`,
            name: thread.name,
            date: thread.lastUpdated,
        }));
    }, [threadsData]);

    return (
        <SimpleGrid cols={{base: 1, sm: 2, lg: 3}} spacing="xl">
            <DashboardLinkList
                title={t("Widget.fillings.title")}
                description={t("Widget.fillings.description")}
                items={mittArbeidItems}
                variant="grouped"
                maxItems={10}
                emptyText={t("Widget.internal.portal.myWork.none")}
                loading={collectionsLoading}
                animationIndex={0}
            />
            <DashboardLinkList
                title={t("Widget.content.title")}
                description={t("Widget.content.description")}
                items={innholdItems}
                variant="pinned-grouped"
                maxItems={10}
                emptyText={t("Widget.internal.portal.history.none")}
                loading={workItemsLoading}
                animationIndex={1}
            />
            <DashboardLinkList
                title={t("Widget.threads.title")}
                description={t("Widget.threads.description")}
                items={sisteTråderItems}
                variant="grouped"
                maxItems={10}
                emptyText={t("Widget.last.threads.none")}
                loading={threadsLoading}
                animationIndex={2}
            />
        </SimpleGrid>
    );
}
